import { Flex } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';
import {
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeader,
} from 'ui-components';
import { getPlatformMetrics } from '~/components/CreatorChannelMetrics';
import { SOCIAL_PLATFORMS, SOCIAL_PLATFORMS_NAMES } from '~/constants';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { useFetchCreatorBenchmarks } from '~/hooks/api/useGetCreatorBenchmarks';
import {
  SupportedCharts,
  getInsightId,
  getIsEmptyData,
  getPlatformStatus,
} from '~/screens/CreatorAudienceDemographics';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution';
import { TalentProfileProperties } from '~/screens/PageBuilder/blocks/TalentProfile';
import { formatDate } from '~/utils';

import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { DemographicsEmptyState } from './DemographicsEmptyState';
import { DisconnectedCard } from './DisconnectedCard';
import { MetricsEmptyState } from './MetricsEmptyState';
import { PlatformDemographics } from './PlatformDemographics';
import { PlatformMetrics } from './PlatformMetrics';
import { usePlatformAnalyticsStore } from './usePlatformAnalyticsStore';

const demographics = ['gender', 'age', 'country'] as SupportedCharts[];

export const ModalPlatformAnalytics = () => {
  const router = useRouter();
  const shareable = useGetMediaKit({ id: router.query.id as string });
  const talentProfile = shareable.data?.blocks.find(
    (s) => s.component === 'TalentProfile',
  );

  const creator = useFetchCreator(
    (talentProfile?.properties as TalentProfileProperties)?.creator?.id,
  );

  const {
    isModalOpen,
    platform,
    reset,
    selectedDemographics,
    selectedMetrics,
    setAllAnalytics,
    callback,
  } = usePlatformAnalyticsStore();

  const insightId =
    getInsightId({
      creator: creator.data,
      platform: platform as SOCIAL_PLATFORMS,
    }) || '';

  const insights = useInsights({
    insightId,
    platform: platform as SOCIAL_PLATFORMS,
  });

  const benchmarks = useFetchCreatorBenchmarks({
    creatorIds: [creator.data?.id],
    managerId: creator.data?.manager?.id,
  });

  const creatorPlatformMetrics = useMemo(() => {
    if (!benchmarks.isSuccess) return;

    return getPlatformMetrics(benchmarks.data?.data || []);
  }, [benchmarks.data?.data, benchmarks.isSuccess]);

  const metricsToShow = (
    creatorPlatformMetrics?.[platform as SOCIAL_PLATFORMS] ?? []
  ).filter((metric) => metric.value != undefined);

  const connectionStatus = getPlatformStatus({
    creator: creator?.data,
    platform: platform as SOCIAL_PLATFORMS,
  });

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
    };
  }, []);

  const getLastUpdatedDate = useMemo(() => {
    if (!benchmarks.isSuccess) return;
    const data = benchmarks.data?.data[0]?.benchmarks;
    // console.log(data);

    if (!data) return;
    if (platform === SOCIAL_PLATFORMS.instagram) return data.igLastUpdatedAt;
    if (platform === SOCIAL_PLATFORMS.tiktok) return data.ttLastUpdatedAt;
    if (platform === SOCIAL_PLATFORMS.youtube) return data.ytLastUpdatedAt;
  }, [benchmarks.data?.data, benchmarks.isSuccess, platform]);

  const handleSave = () => {
    if (typeof callback === 'function') {
      callback(selectedDemographics, selectedMetrics);
    }
    reset();
  };

  const isDisconnected = connectionStatus === 'disconnected';
  const isConnected = connectionStatus === 'connected' || isDisconnected;

  const isEmpty = getIsEmptyData(insights?.data);

  const isChartsDisabled = isEmpty;
  const isMetricsDisabled =
    benchmarks.status === 'error' || !metricsToShow.length;

  const isAllAnalyticsDisabled = isChartsDisabled && isMetricsDisabled;

  const isSelectAllDisabled = (() => {
    if (isMetricsDisabled) {
      // Only check demographics since metrics are disabled
      return selectedDemographics.length === demographics.length;
    }

    if (isChartsDisabled) {
      // Only check metrics since demographics are disabled
      return selectedMetrics.length === metricsToShow.length;
    }

    // Default: Both metrics and demographics are enabled
    return (
      selectedMetrics.length === metricsToShow.length &&
      selectedDemographics.length === demographics.length
    );
  })();

  const handleSelectAll = () => {
    const allDemographics = !isChartsDisabled ? demographics : [];
    const allMetrics = !isMetricsDisabled
      ? metricsToShow.map((m) => m.title)
      : [];

    setAllAnalytics(allDemographics, allMetrics);
  };

  return (
    <Modal isOpen={isModalOpen} onClose={reset} size="xl">
      <ModalHeader>
        Add {SOCIAL_PLATFORMS_NAMES[platform as SOCIAL_PLATFORMS]} analytics
      </ModalHeader>
      <ModalBody>
        {match({
          isConnected,
          isAllAnalyticsDisabled,
          isChartsDisabled,
          isMetricsDisabled,
        })
          // If Platform is not connected, render AnalyticsEmptyState with isConnected false
          .with({ isConnected: false }, () => {
            return (
              <AnalyticsEmptyState
                isDemographicsDisabled={true}
                platform={platform as SOCIAL_PLATFORMS}
                slug={creator.data?.slug}
                isConnected={false}
              />
            );
          })
          // If Audience Demographics and Metrics are unavailable, render AnalyticsEmptyState
          .with({ isAllAnalyticsDisabled: true }, () => {
            return (
              <AnalyticsEmptyState
                isDemographicsDisabled={isChartsDisabled}
                platform={platform as SOCIAL_PLATFORMS}
                slug={creator.data?.slug}
                isConnected={true}
              />
            );
          })
          // If Audience Demographics is unavailable, show DemographicsEmptyState
          .with({ isChartsDisabled: true }, () => (
            <Flex direction="column" gap={6}>
              <DemographicsEmptyState isChartsDisabled={isChartsDisabled} />
              <PlatformMetrics metricsToShow={metricsToShow} />
              {isDisconnected ? (
                <DisconnectedCard
                  lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                  platformFullAccess={creator.data?.platformFullAccess}
                />
              ) : null}
            </Flex>
          ))
          // If Audience Metrics is unavailable, show MetricsEmptyState
          .with({ isMetricsDisabled: true }, () => (
            <Flex direction="column" gap={6}>
              <PlatformDemographics isDemographicsDisabled={isChartsDisabled} />
              <MetricsEmptyState platform={platform as SOCIAL_PLATFORMS} />
              {isDisconnected ? (
                <DisconnectedCard
                  lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                  platformFullAccess={creator.data?.platformFullAccess}
                />
              ) : null}
            </Flex>
          ))
          // If both Audience Demographics and Metrics available, render the components
          .otherwise(() => (
            <Flex direction="column" gap={6}>
              <PlatformDemographics isDemographicsDisabled={isChartsDisabled} />
              <PlatformMetrics metricsToShow={metricsToShow} />
              {isDisconnected ? (
                <DisconnectedCard
                  lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                  platformFullAccess={creator.data?.platformFullAccess}
                />
              ) : null}
            </Flex>
          ))}
      </ModalBody>
      <ModalFooterDefault
        tertiary={{
          ariaLabel: 'Select All',
          isDisabled: isAllAnalyticsDisabled || isSelectAllDisabled,
          onClick: handleSelectAll,
          title: 'Select All',
          trackId: 'media-kit-platform-metrics-select-all-btn',
        }}
        primary={{
          title: 'Save',
          isDisabled: isAllAnalyticsDisabled,
          onClick: handleSave,
          trackId: 'media-kit-platform-metrics-save-btn',
        }}
      />
    </Modal>
  );
};
